import { useRef } from 'react';
import { Layer, Rect, Stage } from 'react-konva';
import type Konva from 'konva';
import type { SceneDoc } from '@system-atlas/shared';

const MAX_W = 180;
const MAX_H = 140;

interface MinimapProps {
  scene: SceneDoc;
  canvas: { x: number; y: number; width: number; height: number };
  viewportWidth: number;
  viewportHeight: number;
  scale: number;
  stagePos: { x: number; y: number };
  onJump: (pos: { x: number; y: number }) => void;
}

/** Übersichtskarte: zeigt die ganze Szene verkleinert; Klick zentriert den Viewport. */
export function Minimap({ scene, canvas, viewportWidth, viewportHeight, scale, stagePos, onJump }: MinimapProps) {
  const stageRef = useRef<Konva.Stage>(null);
  const miniScale = Math.min(MAX_W / canvas.width, MAX_H / canvas.height);
  const w = canvas.width * miniScale;
  const h = canvas.height * miniScale;

  // Sichtbarer Welt-Ausschnitt
  const viewX = -stagePos.x / scale;
  const viewY = -stagePos.y / scale;
  const viewW = viewportWidth / scale;
  const viewH = viewportHeight / scale;

  function handleClick() {
    const stage = stageRef.current;
    const pointer = stage?.getPointerPosition();
    if (!pointer) return;
    const worldX = pointer.x / miniScale + canvas.x;
    const worldY = pointer.y / miniScale + canvas.y;
    onJump({ x: viewportWidth / 2 - worldX * scale, y: viewportHeight / 2 - worldY * scale });
  }

  return (
    <div className="overflow-hidden rounded-md border border-slate-300 bg-white shadow-md dark:border-slate-600">
      <Stage ref={stageRef} width={w} height={h} onMouseDown={handleClick}>
        <Layer listening={false}>
          <Rect x={0} y={0} width={w} height={h} fill="#f8fafc" />
          {scene.nodes.map((n) => (
            <Rect
              key={n.id}
              x={(n.x - canvas.x) * miniScale}
              y={(n.y - canvas.y) * miniScale}
              width={Math.max(2, n.width * miniScale)}
              height={Math.max(2, n.height * miniScale)}
              fill={n.type === 'zone' ? '#cbd5e1' : '#94a3b8'}
              cornerRadius={1}
            />
          ))}
          <Rect
            x={(viewX - canvas.x) * miniScale}
            y={(viewY - canvas.y) * miniScale}
            width={viewW * miniScale}
            height={viewH * miniScale}
            stroke="#2563eb"
            strokeWidth={1.5}
          />
        </Layer>
      </Stage>
    </div>
  );
}
